import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { customersAPI } from '@/lib/api';
import { formatCurrency, formatDate, getPaymentStatusColor } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Phone, Mail, MapPin, Car, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerDetailPage() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customerRes, historyRes] = await Promise.all([
          customersAPI.getById(id),
          customersAPI.getHistory(id)
        ]);
        setCustomer(customerRes.data);
        setHistory(historyRes.data);
      } catch (error) {
        toast.error('Failed to load customer details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Customer not found</p>
        <Link to="/customers">
          <Button variant="outline" className="mt-4">Back to Customers</Button>
        </Link>
      </div>
    );
  }

  const totalSpent = history.reduce((sum, inv) => sum + (inv.grand_total || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/customers">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-black font-heading tracking-tight">{customer.name}</h1>
          <p className="text-muted-foreground">Customer details and invoice history</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <Card data-testid="customer-info-card">
          <CardHeader>
            <CardTitle className="font-heading">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-muted-foreground" />
              <span>{customer.phone}</span>
            </div>
            {customer.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <span>{customer.email}</span>
              </div>
            )}
            {customer.address && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <span>{customer.address}</span>
              </div>
            )}
            <div className="pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Invoices</span>
                <span className="font-semibold">{history.length}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-muted-foreground">Total Spent</span>
                <span className="font-semibold">{formatCurrency(totalSpent)}</span>
              </div>
            </div>
            <Link to={`/vehicles?customer=${customer.id}`}>
              <Button variant="outline" className="w-full mt-4">
                <Car className="w-4 h-4 mr-2" />
                View Vehicles
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Invoice History */}
        <div className="lg:col-span-2">
          <Card data-testid="customer-history-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Invoice History</CardTitle>
              <Link to={`/invoices/new?customer=${customer.id}`}>
                <Button size="sm">New Invoice</Button>
              </Link>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-3">
                  {history.map((invoice) => (
                    <Link
                      key={invoice.id}
                      to={`/invoices/${invoice.id}`}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                      data-testid={`history-invoice-${invoice.invoice_no}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-mono font-medium text-sm">{invoice.invoice_no}</p>
                          <p className="text-sm text-muted-foreground">
                            {invoice.vehicle_number} • {formatDate(invoice.date)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(invoice.grand_total)}</p>
                        <Badge className={getPaymentStatusColor(invoice.payment_status)}>
                          {invoice.payment_status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto opacity-50" />
                  <p className="mt-2">No invoices yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
